/**
 * 앱 전역 메뉴 설정 — purpose-centered IA (Primary 1)
 *
 * SSOT remount:
 *  - domainRouteConfig → deriveDomainNavigation builders
 *  - purposeCenteredNavigation remounts under 7 primary areas + advanced layers
 *  - Canon: @shared/frontend-experience-architecture-and-navigation-canon
 *
 * Stream U3 — Nav & Language:
 *  - type:'group' 아이템은 시각적 그룹 헤더 (Ant Design type:'group', 비선택·비탐색)
 *  - adminOnly:true 아이템은 관리자 전용 (AppSidebar에서 필터)
 *  - flattenMenuItems / findMenuKeyPath: group 투명 처리 (key path 불포함)
 */

import React from 'react';
import {
  isPathHiddenByPilotFold,
  pathMatchesAnyPilotRouteHint,
  type PilotSurfaceGuardState,
} from '@shared/platform/pilot-surface-guard';
import {
  PRIMARY_NAVIGATION_AREAS_V0,
  ADVANCED_NAVIGATION_LAYERS_V0,
} from '@shared/frontend-experience-architecture-and-navigation-canon';
import {
  TeamOutlined,
  ShoppingOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  BookOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  SoundOutlined,
  ToolOutlined,
  SearchOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  AuditOutlined,
  ControlOutlined,
  DeploymentUnitOutlined,
} from '@ant-design/icons';
import {
  buildPurposeCenteredDomainItems,
  buildPurposeCenteredFooterItems,
} from './purposeCenteredNavigation';

/** 전역 검색 사이드바 항목 키 (경로 없음 — AppSidebar에서 모달/포커스 처리) */
export const GLOBAL_SEARCH_MENU_KEY = 'global-search';

export interface AppMenuItem {
  key: string;
  path: string;
  icon: React.ReactNode;
  label: string;
  children?: AppMenuItem[];
  /**
   * Ant Design type:'group' — 시각적 그룹 헤더.
   * path 불필요, 선택·탐색 대상 아님.
   * flattenMenuItems·findMenuKeyPath에서 투명 처리됨.
   */
  type?: 'group';
  /**
   * 관리자 전용 항목 — AppSidebar filterAdminOnlyItems에서 필터링.
   */
  adminOnly?: boolean;
}

/** Purpose-centered primary area keys (Hick ≤7) — machine canon aligned. */
export const NAV_AREA = Object.fromEntries(
  PRIMARY_NAVIGATION_AREAS_V0.map((area) => [area.areaId, area.navKey])
) as Record<(typeof PRIMARY_NAVIGATION_AREAS_V0)[number]['areaId'], string>;

/** Role-gated advanced layers (not primary Hick-7). */
export const NAV_LAYER = Object.fromEntries(
  ADVANCED_NAVIGATION_LAYERS_V0.map((area) => [area.areaId, area.navKey])
) as Record<(typeof ADVANCED_NAVIGATION_LAYERS_V0)[number]['areaId'], string>;

/** @deprecated ERP-domain group keys — superseded by NAV_AREA. Kept for transitional imports. */
export const NAV_GROUP = {
  operations: NAV_AREA.operations,
  finance: NAV_AREA.operations,
  growth: NAV_AREA.objects,
  knowledge: NAV_AREA.objects,
  space: NAV_AREA.objects,
  production: NAV_AREA.operations,
  data: NAV_AREA.operations,
} as const;

/** 상단 — 목적 중심 7 primary areas (리프 key·path는 domainRouteConfig SSOT 유지) */
export const SIDEBAR_DOMAIN_ITEMS: AppMenuItem[] = buildPurposeCenteredDomainItems({
  today: <DashboardOutlined />,
  workbench: <AppstoreOutlined />,
  operations: <DeploymentUnitOutlined />,
  objects: <ShoppingOutlined />,
  evidence: <AuditOutlined />,
  people: <TeamOutlined />,
  automation: <ThunderboltOutlined />,
  manufacturing: <ToolOutlined />,
  finance: <DollarOutlined />,
  log: <UnorderedListOutlined />,
  item: <ShoppingOutlined />,
  space: <EnvironmentOutlined />,
  knowledge: <BookOutlined />,
  calendar: <CalendarOutlined />,
  appeal: <SoundOutlined />,
});

/** 전역 검색 (TopBar GlobalSearchInput · ⌘K 전용 — 사이드바 항목 제거됨) */
export const GLOBAL_SEARCH_SIDEBAR_ITEM: AppMenuItem = {
  key: GLOBAL_SEARCH_MENU_KEY,
  path: '',
  icon: <SearchOutlined />,
  label: '검색',
};

/**
 * 하단 구분선 후: 구성(Configure) · 운영 콘솔(Operations Console)
 * 일반 사용자 primary nav에 포함하지 않음 (adminOnly layer).
 */
export const SIDEBAR_FOOTER_ITEMS: AppMenuItem[] = buildPurposeCenteredFooterItems({
  configure: <SettingOutlined />,
  console: <ControlOutlined />,
});

/** pathname에서 사이드바 selectedKey 도출 (가장 구체적인 경로 우선) */
export function flattenMenuItems(items: AppMenuItem[]): AppMenuItem[] {
  return items.flatMap((item) => {
    if (item.type === 'group') {
      // group 아이템 자체는 비탐색 — children만 flat으로 전개
      return flattenMenuItems(item.children ?? []);
    }
    return [item, ...flattenMenuItems(item.children ?? [])];
  });
}

/** 메뉴 트리에서 targetKey까지의 key 경로 (루트→리프, group 투명 처리) */
export function findMenuKeyPath(
  items: AppMenuItem[],
  targetKey: string,
  path: string[] = []
): string[] | null {
  for (const item of items) {
    if (item.type === 'group') {
      // group은 key path에 포함하지 않고 children만 탐색
      const found = findMenuKeyPath(item.children ?? [], targetKey, path);
      if (found) return found;
      continue;
    }
    const currentPath = [...path, item.key];
    if (item.key === targetKey) {
      return currentPath;
    }
    if (item.children?.length) {
      const found = findMenuKeyPath(item.children, targetKey, currentPath);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/** Ant Design SubMenu openKeys — 리프면 조상만, 부모 메뉴면 자신까지 포함 */
export function openKeysFromMenuPath(keyPath: string[] | null, items: AppMenuItem[]): string[] {
  if (!keyPath?.length) {
    return [];
  }
  const flat = flattenMenuItems(items);
  const lastKey = keyPath[keyPath.length - 1];
  const lastItem = flat.find((item) => item.key === lastKey);
  const hasChildren = (lastItem?.children?.length ?? 0) > 0;
  return hasChildren ? keyPath : keyPath.slice(0, -1);
}

export interface SidebarOpenKeys {
  domainOpenKeys: string[];
  footerOpenKeys: string[];
}

/** 현재 pathname 기준 — domain·footer Menu 각각 펼칠 SubMenu key */
export function getSidebarOpenKeys(pathname: string): SidebarOpenKeys {
  const selectedKey = getSidebarSelectedKey(pathname);
  if (!selectedKey) {
    return { domainOpenKeys: [], footerOpenKeys: [] };
  }
  const domainPath = findMenuKeyPath(SIDEBAR_DOMAIN_ITEMS, selectedKey);
  const footerPath = findMenuKeyPath(SIDEBAR_FOOTER_ITEMS, selectedKey);
  return {
    domainOpenKeys: openKeysFromMenuPath(domainPath, SIDEBAR_DOMAIN_ITEMS),
    footerOpenKeys: openKeysFromMenuPath(footerPath, SIDEBAR_FOOTER_ITEMS),
  };
}

export function getSidebarSelectedKey(pathname: string): string {
  const allItems = [...SIDEBAR_DOMAIN_ITEMS, ...SIDEBAR_FOOTER_ITEMS];
  const flatItems = flattenMenuItems(allItems);

  let bestKey = '';
  let bestPathLen = -1;

  for (const item of flatItems) {
    if (!item.path) {
      continue;
    }

    const matches = pathname === item.path || pathname.startsWith(`${item.path}/`);

    if (matches && item.path.length >= bestPathLen) {
      bestKey = item.key;
      bestPathLen = item.path.length;
    }
  }

  if (pathname.startsWith('/finance/voucher')) {
    return 'finance-vouchers';
  }

  if (
    pathname.startsWith('/approvals/') &&
    pathname !== '/approvals/pending' &&
    !pathname.startsWith('/approvals/pending/') &&
    pathname !== '/approvals/history' &&
    !pathname.startsWith('/approvals/history/')
  ) {
    return 'flow-approvals-pending';
  }

  if (pathname === '/entity/external/create') {
    return 'entity-external-create';
  }

  if (bestKey) {
    return bestKey;
  }

  if (pathname.startsWith('/conscious/')) {
    return 'conscious-hub';
  }

  return '';
}

function isMenuPathFolded(path: string, guard: PilotSurfaceGuardState): boolean {
  if (!guard.pilotModeActive || !path) {
    return false;
  }
  if (isPathHiddenByPilotFold(path, guard)) {
    return true;
  }
  return !pathMatchesAnyPilotRouteHint(path, guard.visibleRoutes);
}

/**
 * P1b — pilotMode active 시 killer-loop visible route hints 외 메뉴 항목 제거.
 * @see src/shared/platform/pilot-surface-guard.ts
 */
export function filterPilotFoldedMenuItems(
  items: AppMenuItem[],
  guard: PilotSurfaceGuardState
): AppMenuItem[] {
  if (!guard.pilotModeActive) {
    return items;
  }

  return items.flatMap((item) => {
    if (item.type === 'group') {
      const children = filterPilotFoldedMenuItems(item.children ?? [], guard);
      return children.length > 0 ? [{ ...item, children }] : [];
    }

    if (item.children?.length) {
      const children = filterPilotFoldedMenuItems(item.children, guard);
      if (children.length > 0) {
        return [{ ...item, children }];
      }
      if (item.path && !isMenuPathFolded(item.path, guard)) {
        const { children: _children, ...leaf } = item;
        return [leaf];
      }
      return [];
    }

    if (!item.path || isMenuPathFolded(item.path, guard)) {
      return [];
    }

    return [item];
  });
}

/**
 * adminOnly 항목 필터링 헬퍼 (AppSidebar에서 호출)
 * isAdmin = true면 모두 노출, false면 adminOnly:true 항목 제거
 */
export function filterAdminOnlyItems(items: AppMenuItem[], isAdmin: boolean): AppMenuItem[] {
  if (isAdmin) return items;
  return items.flatMap((item) => {
    if (item.adminOnly) return [];
    if (item.children?.length) {
      return [{ ...item, children: filterAdminOnlyItems(item.children, isAdmin) }];
    }
    return [item];
  });
}
